// Lazy loading utilities for heavy dependencies

// Lazy load Mixpanel
export const loadMixpanel = () => import(/* webpackChunkName: "mixpanel" */ 'mixpanel-browser');

// Lazy load Adjust SDK
export const loadAdjust = () => import(/* webpackChunkName: "adjust" */ '@adjustcom/adjust-web-sdk');

// Lazy load video players based on need
export const loadShakaPlayer = () => import(/* webpackChunkName: "shaka-player" */ 'shaka-player' as any);

// Cache loaded modules
const moduleCache = new Map<string, any>();

// Generic lazy loader with caching
export async function lazyLoadModule<T>(loader: () => Promise<{ default: T } | T>, cacheKey: string): Promise<T> {
  // Check cache first
  if (moduleCache.has(cacheKey)) {
    return moduleCache.get(cacheKey);
  }

  try {
    const module = await loader();
    const result = module && typeof module === 'object' && 'default' in module ? (module as any).default : module;
    moduleCache.set(cacheKey, result);
    return result as T;
  } catch (error) {
    console.error(`Failed to lazy load module ${cacheKey}:`, error);
    throw error;
  }
}

// Initialize analytics lazily
export async function initAnalytics() {
  try {
    // Load analytics SDKs in parallel but handle failures gracefully
    const [mixpanel, adjust] = await Promise.allSettled([
      lazyLoadModule(loadMixpanel, 'mixpanel'),
      lazyLoadModule(loadAdjust, 'adjust'),
    ]);

    const analytics: any = {};

    if (mixpanel.status === 'fulfilled') {
      analytics.mixpanel = mixpanel.value;
    }

    if (adjust.status === 'fulfilled') {
      analytics.adjust = adjust.value;
    }

    return analytics;
  } catch (error) {
    console.warn('Failed to initialize analytics:', error);
    return {};
  }
}

// Load video player based on type
export async function loadVideoPlayer(type: 'shaka') {
  switch (type) {
    case 'shaka':
      return lazyLoadModule(loadShakaPlayer, 'shaka-player');
    default:
      throw new Error(`Unknown player type: ${type}`);
  }
}

// Preload critical modules in the background
export function preloadCriticalModules() {
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 1000);
    }
  };

  schedulePreload(() => {
    // Preload video player (most likely to be used)
    loadShakaPlayer().catch(err => console.warn('Failed to preload Shaka player:', err));
  });
}
